import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { getMinedTokensAPI } from "../../api/miningApi";

const fmt = (n) =>
  typeof n === "number" ? n.toLocaleString(undefined, { maximumFractionDigits: 2 }) : n ?? "-";

export default function MinedTokens() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRows, setTotalRows] = useState(0);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getMinedTokensAPI(page, pageSize);
      if (res.status) {
        setRows(res.data.data || []);
        setTotalPages(res.data.totalPages || 1);
        setTotalRows(res.data.totalRows || 0);
      } else {
        setError("Failed to fetch mined tokens");
      }
    } catch (err) {
      setError("Failed to fetch mined tokens");
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const renderPagination = () =>
    Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
      if (p === 1 || p === totalPages || (p >= page - 1 && p <= page + 1)) {
        return (
          <button
            key={p}
            onClick={() => setPage(p)}
            className={`h-8 w-8 rounded text-xs font-medium transition-colors ${
              p === page
                ? "bg-ginox-green/20 border border-ginox-green/60 text-ginox-green"
                : "border border-border/70 bg-white/[0.02] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground"
            }`}
          >
            {p}
          </button>
        );
      }
      if (p === page - 2 || p === page + 2) {
        return (
          <span key={p} className="h-8 w-8 flex items-center justify-center text-muted-foreground text-xs">
            ...
          </span>
        );
      }
      return null;
    });

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-400 text-base mb-4">{error}</p>
          <button
            onClick={fetchData}
            className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const columns = [
    "User ID",
    "Total Amount",
    "Mined Tokens",
    "Referral Mined",
    "Daily Reward",
    "Social Channel",
  ];

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
          <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
          Mining
        </div>
        <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">
          Mined <span className="text-gradient-ginox">Tokens</span>
        </h1>
        <p className="text-sm text-muted-foreground">
          {loading ? "Loading..." : `${totalRows.toLocaleString()} users`}
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              {columns.map((h) => (
                <th key={h} className="border-b border-border/70 px-4 py-3 text-left text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="py-12 text-center text-sm text-muted-foreground">Loading...</td>
              </tr>
            ) : rows.length > 0 ? (
              rows.map((r) => (
                <tr key={r.userId} className="group hover:bg-white/[0.02] transition-colors">
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{r.userId}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{fmt(r.totalAmount)}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-muted-foreground font-mono">{fmt(r.mined_tokens)}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-muted-foreground font-mono">{fmt(r.referral_mined_tokens)}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-muted-foreground font-mono">{fmt(r.daily_reward)}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-muted-foreground font-mono">{fmt(r.social_channel)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="py-12 text-center text-sm text-muted-foreground">No data found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex flex-col items-center gap-3 md:flex-row md:items-center md:justify-between">
          <p className="text-xs text-muted-foreground">
            Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, totalRows)} of {totalRows} users
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              className="h-8 w-8 rounded border border-border/70 bg-white/[0.02] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            {renderPagination()}
            <button
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
              className="h-8 w-8 rounded border border-border/70 bg-white/[0.02] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
